import { LitElement, html, css } from 'lit';

import { CSS_COLOR_BLUE_1 } from '../../../../packages/neb-lit-components/src/components/neb-button';
import { hasFeatureOrBeta } from '../../../../packages/neb-utils/feature-util';
import {
  baseStyles,
  CSS_COLOR_GREY_3,
  CSS_COLOR_GREY_9,
  CSS_COLOR_WHITE,
  CSS_FONT_FAMILY,
  CSS_FONT_SIZE_BODY,
  CSS_SMALL_SPACING,
} from '../../../styles';
import '../../misc/neb-icon';

export const ELEMENTS = {
  dropdownHeader: { id: 'dropdown-header' },
  container: { id: 'container' },
  icon: { id: 'icon' },
  iconTrailing: { id: 'icon-trailing' },
  menuItems: { id: 'menuItems' },
  buttonItems: { selector: '[id^=button-items-]' },
  label: { selector: '[id^=label-]' },
  iconItem: { selector: '[id^=icon-item]' },
};

class NebButtonDropdown extends LitElement {
  static get properties() {
    return {
      __open: {
        reflect: true,
        type: Boolean,
      },
      items: {
        type: Array,
      },
      icon: {
        type: String,
      },
    };
  }

  static get styles() {
    return [
      baseStyles,
      css`
        :host {
          display: inline-block;
          position: relative;
        }

        .container {
          position: relative;
          width: 100%;
        }

        .dropdown-header {
          cursor: pointer;
          display: flex;
          flex: 1;
          width: 100%;
          border-radius: 5px;
          padding: 0 5px;
          align-items: center;
          height: 36px;
          border: 1px solid ${CSS_COLOR_GREY_9};
          background-color: ${CSS_COLOR_WHITE};
          position: relative;
        }

        .button-item {
          cursor: pointer;
          padding: 10px;
          background: transparent;
          border: none;
          width: 100%;
        }

        .button-item:hover {
          background: ${CSS_COLOR_GREY_3};
        }

        .button-content {
          display: flex;
          align-items: center;
        }

        .disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .icon {
          width: 20px;
          height: 20px;
          fill: ${CSS_COLOR_BLUE_1};
        }

        .trailing-icon {
          height: 13px;
          width: 13px;
          transform: rotate(0);
          transition: transform 100ms;
          margin-left: 5px;
        }

        .label {
          position: relative;
          padding: 0 ${CSS_SMALL_SPACING};
          color: ${CSS_COLOR_BLUE_1};
          font-size: ${CSS_FONT_SIZE_BODY};
          font-family: ${CSS_FONT_FAMILY};
          text-align: center;
        }

        .menu-items {
          display: flex;
          flex-direction: column;
          position: absolute;
          left: 0;
          top: 100%;
          z-index: 10;
          background: ${CSS_COLOR_WHITE};
          min-width: max-content;
          width: auto;
          box-shadow: 0 1px 3px #00000033;
        }

        .menu-items-container {
          position: absolute;
          left: 0;
          top: 100%;
          z-index: 1000;
        }

        .menu-animate {
          position: relative;
          transform: scale(0.9);
          opacity: 0;
          z-index: 1000;
          transition:
            transform 200ms cubic-bezier(0.175, 0.885, 0.32, 1.275),
            opacity 200ms;
          pointer-events: none;
          visibility: hidden;
        }

        .menu-animate.open {
          transform: scale(1);
          opacity: 1;
          pointer-events: auto;
          visibility: visible;
        }

        :host([__open]) .trailing-icon {
          transform: rotate(180deg);
        }
      `,
    ];
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.items = [];
    this.icon = '';

    this.__open = false;
  }

  __initHandlers() {
    this.__handlers = {
      expand: () => {
        this.__open = !this.__open;
      },
      mouseEnter: () => {
        this.__open = true;
      },
      mouseLeave: () => {
        this.__open = false;
      },
      onClickMenuItem: async item => {
        if (item.disabled) return;
        await item.onClick();
        this.__open = false;
      },
    };
  }

  __checkFeatureFlags() {
    const items = [];

    this.items.forEach(async item => {
      const result = await hasFeatureOrBeta(item.featureFlag);

      if (result || !item.featureFlag) {
        items.push(item);
      }
    });

    this.items = items;
  }

  update(changedProps) {
    if (changedProps.has('items')) {
      this.__checkFeatureFlags();
    }

    super.update(changedProps);
  }

  __renderDropdownHeader() {
    return html`
      <button
        id="${ELEMENTS.dropdownHeader.id}"
        class="dropdown-header"
        @click="${this.__handlers.expand}"
      >
        <neb-icon
          id="${ELEMENTS.icon.id}"
          class="icon"
          icon=${this.icon}
        ></neb-icon>
        <neb-icon
          id="${ELEMENTS.iconTrailing.id}"
          class="icon trailing-icon"
          icon="neb:chevron"
        ></neb-icon>
      </button>
      ${this.__renderMenu()}
    `;
  }

  __renderIcon(item, index) {
    return item.icon
      ? html`<neb-icon
          id="icon-item-${index}"
          class="icon"
          icon=${item.icon}
        ></neb-icon>`
      : '';
  }

  __renderLabel(item, index) {
    return html`<span id="label-${index}" class="label">${item.label}</span>`;
  }

  __renderMenu() {
    const { items } = this;
    const menu = [];

    items.forEach((item, index) => {
      menu.push(html`
        <button
          id="button-items-${index}"
          class="button-item ${item.disabled ? 'disabled' : ''}"
          @click="${() => this.__handlers.onClickMenuItem(item)}"
          ?disabled="${item.disabled}"
        >
          <div class="button-content">
            ${this.__renderIcon(item, index)} ${this.__renderLabel(item, index)}
          </div>
        </button>
      `);
    });

    return html`
      <div class="menu-items-container">
        <div
          id="${ELEMENTS.menuItems.id}"
          class="menu-animate${this.__open ? ' open' : ''}"
        >
          <div class="menu-items">${menu}</div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div
        id="${ELEMENTS.container.id}"
        class="container"
        @mouseenter="${this.__handlers.mouseEnter}"
        @mouseleave="${this.__handlers.mouseLeave}"
      >
        ${this.__renderDropdownHeader()}
      </div>
    `;
  }
}

customElements.define('neb-button-dropdown', NebButtonDropdown);
